// ─────────────────────────────────────────────────────────────────────────────
// GUEST SPACE v0 (Ticket 9 deliverable 3) — a per-guest page assembled from
// what exists: their episodes (candidate cards filtered by people[]), a
// Reflections-built profile block where one exists, and a follow affordance.
//
// Everything this page fetches is a bare same-origin static GET (the show
// fence) — the guest page is publisher-published, identical for every
// visitor, and involves no profile, no ranking, no member service.
//
// CONSENT: profile blocks pass consentFilterBlocks (public-only) before
// render — see src/lib/guests.ts. A guest without a profile degrades cleanly
// to episodes + follow.
// ─────────────────────────────────────────────────────────────────────────────

import {useEffect, useState} from 'react'

import {loadCandidateSet, type Card} from '../lib/candidateSet'
import {
  assembleGuestSpace,
  emailCaptureEnabled,
  loadGuestProfile,
  loadGuestRegistry,
  type GuestSpaceModel,
} from '../lib/guests'
import {BRAND, CARD, CONCEPT_PILL, FOOTER, GUEST_PAGE} from './showContent'

export function GuestSpace({slug}: {slug: string}) {
  const [model, setModel] = useState<GuestSpaceModel | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const [registry, set] = await Promise.all([loadGuestRegistry(), loadCandidateSet()])
        const guest = registry.find(g => g.slug === slug)
        if (!guest) throw new Error('No such guest page.')
        const profile = guest.profile ? await loadGuestProfile(slug) : null
        if (!live) return
        setModel(assembleGuestSpace(guest, set.cards, profile, {emailCapture: emailCaptureEnabled()}))
      } catch (err) {
        if (live) setError(String((err as Error)?.message ?? err))
      }
    })()
    return () => {
      live = false
    }
  }, [slug])

  if (error) {
    return (
      <div className="wrap show">
        <GuestHeader />
        <p className="notice notice--warn">{error}</p>
        <a className="watch" href="#/">
          {GUEST_PAGE.back}
        </a>
      </div>
    )
  }
  if (!model) {
    return (
      <div className="wrap show">
        <GuestHeader />
        <p className="notice">Pouring…</p>
      </div>
    )
  }

  const {guest, episodes, profile, follow} = model

  return (
    <div className="wrap show">
      <GuestHeader />

      <section className="hero">
        <div>
          <div className="eyebrow">
            <a href="#/">{GUEST_PAGE.back}</a>
          </div>
          <h1 className="disp">{guest.name}</h1>
          <p className="lede">{guest.note}</p>
          <Follow follow={follow} name={guest.name} />
        </div>
        <div className="concierge" aria-label="Guest">
          <div className="who">
            <div className="dot">{guest.initials}</div>
            <div>
              <b>{guest.name}</b>
              <small>
                {episodes.length} {episodes.length === 1 ? 'episode' : 'episodes'} on the show
              </small>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="sec-head">
          <h2 className="disp">{GUEST_PAGE.storiesHead}</h2>
          <div className="sub">{GUEST_PAGE.storiesSub}</div>
        </div>
        {profile && profile.blocks.length ? (
          <div className="shelf">
            {profile.blocks.map((b, i) => (
              <article className="ep" key={i}>
                {b.title ? <h3>{b.title}</h3> : null}
                {b.text ? <blockquote>{b.text}</blockquote> : null}
              </article>
            ))}
          </div>
        ) : (
          <p className="notice">{GUEST_PAGE.noProfile}</p>
        )}
      </section>

      <section>
        <div className="sec-head">
          <h2 className="disp">{GUEST_PAGE.episodesHead}</h2>
          <div className="sub">{GUEST_PAGE.episodesSub}</div>
        </div>
        <div className="shelf">
          {episodes.map((card, i) => (
            <GuestEpisodeCard card={card} key={`${card.id}-${i}`} />
          ))}
        </div>
      </section>

      <footer>
        <div className="promise">
          <b>{FOOTER.promiseLead}</b>
          {FOOTER.promiseBody}
        </div>
      </footer>
    </div>
  )
}

function GuestHeader() {
  return (
    <header>
      <div className="brand">
        <a className="mark disp" href="#/" style={{textDecoration: 'none'}}>
          {BRAND.pre} <b>{BRAND.mid}</b> {BRAND.post}
        </a>
        <div className="concept">{CONCEPT_PILL}</div>
      </div>
    </header>
  )
}

/**
 * The follow affordance:
 *   atproto        the guest is on-network — deep link to their One profile
 *   email-capture  FLAG-DARK fallback (renders only when the flag is on, and
 *                  is a mailto: draft — no capture endpoint exists in v0, so
 *                  no address is ever stored anywhere)
 *   none           honest copy; no dead button
 */
function Follow({follow, name}: {follow: GuestSpaceModel['follow']; name: string}) {
  if (follow.kind === 'atproto') {
    return (
      <a className="watch" href={`https://app.authority-one.com/profile/${encodeURIComponent(follow.did)}`}>
        {GUEST_PAGE.followOn(name)}
      </a>
    )
  }
  if (follow.kind === 'email-capture') {
    return (
      <a className="watch" href={`mailto:follow@betweentwobeers.example?subject=${encodeURIComponent(`Follow ${name}`)}`}>
        {GUEST_PAGE.followEmail(name)}
      </a>
    )
  }
  return <p className="notice">{GUEST_PAGE.followNone}</p>
}

function GuestEpisodeCard({card}: {card: Card}) {
  return (
    <article className="ep">
      <div className="tag">{(card.topics ?? []).join(' · ')}</div>
      <h3>{card.title}</h3>
      {card.summary ? <blockquote>{card.summary}</blockquote> : null}
      <div className="row">
        <a className="watch" href={card.ref?.url ?? '#'} target="_blank" rel="noreferrer noopener">
          {CARD.watch}
        </a>
      </div>
    </article>
  )
}
